import { APIError, fetcher } from "@certn/api";
import useSWR from "swr";
import {
  showToast,
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Stack,
  Text,
} from "@certn/cascadia";
import {
  BasicButton,
  FileUpload,
  Loader,
  SectionMessage,
  Space,
  Signature,
} from "@certn/cascadia-react";
import { CertnDocumentScroll } from "@certn/cascadia-react/miscellaneous/contract/CertnDocumentScroll";
import {
  CascadiaControlProps,
  CascadiaJsonFormContext,
  withCascadiaControlProps,
} from "@certn/forms";
import { DEFAULT_FILE_TYPES } from "@certn/utils";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/react";
import {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useTranslation, Trans } from "react-i18next";
import { useParams } from "react-router-dom";

import { ConsentRequiredModal } from "./ConsentRequiredModal";
import { useApplication } from "../../ApplicationContext";
import { AfpConsentLayoutContext } from "../../layouts/AfpConsentLayoutContext";

// API Response types
interface AfpConsentDownloadResponse {
  presigned_url: string;
}

interface AfpConsentStatusResponse {
  is_under_18: boolean;
  is_signed: boolean;
  is_complete: boolean;
  signed_form_download_url?: string;
}

interface AfpConsentSignRequest {
  applicant_signature: string; // base64 encoded image
  guardian_signature?: string; // optional, for under-18 if parent signs digitally
}

interface AfpConsentSignResponse {
  signed: boolean;
  is_under_18: boolean;
  consent_complete: boolean;
  signed_form_download_url?: string;
}

interface AfpConsentCompleteResponse {
  success?: boolean;
  consent_complete?: boolean;
  error?: string;
}

// File info type for upload
interface FileInfo {
  key?: string;
  name: string;
  size?: number;
  type?: string;
  url?: string;
}

// Consent flow states
enum ConsentFlowState {
  INITIAL = "INITIAL", // Not yet signed
  SIGNING = "SIGNING", // Currently signing
  PENDING_PARENT_SIGNATURE = "PENDING_PARENT_SIGNATURE", // Under-18 has signed, waiting for parent
  COMPLETING = "COMPLETING", // Calling complete endpoint
  COMPLETE = "COMPLETE", // Done
}

// Upload type for AFP consent documents
enum AfpUploadType {
  CONSENT_DOCUMENT = "AFP_CONSENT_DOCUMENT",
}

export function AfpConsentControl(props: CascadiaControlProps) {
  const { handleChange, path } = props;
  const { t } = useTranslation();
  const { caseId } = useParams();
  const { isSubmitted, submit } = useContext(CascadiaJsonFormContext);
  const { subLabel } = useContext(AfpConsentLayoutContext);
  const { navigateToSection, prevSection, partiallySaveData } =
    useApplication();

  // Flow state management
  const [flowState, setFlowState] = useState<ConsentFlowState>(
    ConsentFlowState.INITIAL
  );
  const [signatureData, setSignatureData] = useState<string>(""); // Base64 signature
  const [showConsentRequiredModal, setShowConsentRequiredModal] =
    useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<FileInfo[]>([]);
  // Store signed form download URL from sign response
  const [signedFormUrl, setSignedFormUrl] = useState<string | undefined>();
  // Track if user has reviewed/acknowledged the document
  const [documentReviewed, setDocumentReviewed] = useState(false);

  // Ref to prevent state reset during submission (refs update synchronously)
  const isSubmittingRef = useRef(false);

  const hasSignature = !!signatureData;

  // Reusable privacy message component
  const PrivacyMessage = useMemo(
    () => (
      <SectionMessage
        icon="verified_user"
        sectionTitle={t("Your privacy is important to us")}
        type="success"
      >
        {t(
          "Certn will only collect information that is needed to complete your check."
        )}
      </SectionMessage>
    ),
    [t]
  );

  // Fetch pre-populated consent form PDF
  const { data: downloadData, isLoading: isPdfLoading } =
    useSWR<AfpConsentDownloadResponse>(
      caseId
        ? `/api/internal/applicant/case/${caseId}/afp-consent/download/`
        : null,
      fetcher.request
    );

  // Fetch consent status
  const {
    data: statusData,
    isLoading: isStatusLoading,
    mutate: refetchStatus,
  } = useSWR<AfpConsentStatusResponse>(
    caseId
      ? `/api/internal/applicant/case/${caseId}/afp-consent/status/`
      : null,
    fetcher.request
  );

  const presignedUrl = downloadData?.presigned_url || "";

  // Memoized derived values
  const isTransitioning =
    flowState === ConsentFlowState.SIGNING ||
    flowState === ConsentFlowState.COMPLETING;

  const isNextDisabled = useMemo(
    () =>
      isTransitioning ||
      (flowState === ConsentFlowState.INITIAL && !hasSignature) ||
      (flowState === ConsentFlowState.PENDING_PARENT_SIGNATURE &&
        uploadedFiles.length === 0),
    [isTransitioning, flowState, hasSignature, uploadedFiles.length]
  );

  const nextButtonText = useMemo(() => {
    if (flowState === ConsentFlowState.PENDING_PARENT_SIGNATURE) {
      return t("Complete Consent");
    }
    if (flowState === ConsentFlowState.INITIAL && hasSignature) {
      return t("Sign");
    }
    return t("Next step");
  }, [flowState, hasSignature, t]);

  // Reusable document review label
  const documentReviewLabel = t(
    "I have reviewed the above information and confirm it is correct."
  );

  // Helper to update consent state (internal state only)
  const updateConsentState = useCallback(
    (state: ConsentFlowState, reviewed: boolean, signedUrl?: string) => {
      setFlowState(state);
      setDocumentReviewed(reviewed);
      setSignedFormUrl(signedUrl);
      if (!reviewed) setSignatureData("");
    },
    []
  );

  // Initialize state from backend status
  useEffect(() => {
    if (!statusData) return;

    // Don't reset state if we're currently submitting (ref updates synchronously)
    if (isSubmittingRef.current) {
      return;
    }

    const { is_complete, is_signed, is_under_18, signed_form_download_url } =
      statusData;
    const isComplete = is_complete || (is_signed && !is_under_18);

    if (isComplete) {
      updateConsentState(ConsentFlowState.COMPLETE, true);
      handleChange(path, true);
    } else if (is_signed && is_under_18) {
      updateConsentState(
        ConsentFlowState.PENDING_PARENT_SIGNATURE,
        true,
        signed_form_download_url
      );
    } else {
      updateConsentState(ConsentFlowState.INITIAL, false);
      handleChange(path, false);
    }
  }, [statusData, handleChange, path, updateConsentState]);

  // Show consent required modal when trying to submit without signing
  useEffect(() => {
    if (
      isSubmitted &&
      flowState === ConsentFlowState.INITIAL &&
      !hasSignature
    ) {
      setShowConsentRequiredModal(true);
    }
  }, [isSubmitted, flowState, hasSignature]);

  // Handle signature change from signature pad - captures base64 data locally
  const handleSignatureChange = useCallback((signature: string) => {
    setSignatureData(signature);
    setDocumentReviewed(true);
  }, []);

  // Handle manual checkbox click for document review
  const handleDocumentReviewChange = useCallback((isSelected: boolean) => {
    setDocumentReviewed(isSelected);
  }, []);

  // Handle signature clear
  const handleSignatureClear = useCallback(() => {
    setSignatureData("");
    setDocumentReviewed(false);
  }, []);

  // Sign the consent form - called when user clicks "Sign" button
  const handleSign = async () => {
    if (!caseId || !signatureData) return;

    isSubmittingRef.current = true;
    setFlowState(ConsentFlowState.SIGNING);
    handleChange(path, true);

    try {
      const requestBody: AfpConsentSignRequest = {
        applicant_signature: signatureData,
      };

      const response = await fetcher.submit<AfpConsentSignResponse>(
        `/api/internal/applicant/case/${caseId}/afp-consent/create-claim/`,
        requestBody
      );

      if (response.is_under_18) {
        // Under-18 - need parent signature
        handleChange(path, false);
        setFlowState(ConsentFlowState.PENDING_PARENT_SIGNATURE);
        if (response.signed_form_download_url) {
          setSignedFormUrl(response.signed_form_download_url);
        }
        isSubmittingRef.current = false;
        await refetchStatus();
      } else {
        // Adult (18+) - consent complete, ready for next step
        setFlowState(ConsentFlowState.COMPLETE);
        // Keep isSubmittingRef true until user navigates away
      }
    } catch (e) {
      console.error("Error signing AFP consent", e);
      isSubmittingRef.current = false;
      handleChange(path, false);
      setFlowState(ConsentFlowState.INITIAL);
      showToast(
        t("Something went wrong signing the consent form. Please try again."),
        {
          type: "error",
        }
      );
      Sentry.captureException(e);
    }
  };

  // Complete consent (under-18 after uploading parent-signed form)
  const handleComplete = async () => {
    if (!caseId) return;

    if (uploadedFiles.length === 0) {
      showToast(t("Please upload the parent-signed consent form first."), {
        type: "error",
      });
      return;
    }

    isSubmittingRef.current = true;
    setFlowState(ConsentFlowState.COMPLETING);

    try {
      const response = await fetcher.submit<AfpConsentCompleteResponse>(
        `/api/internal/applicant/case/${caseId}/afp-consent/complete/`,
        {}
      );

      if (response.error) {
        // Explicit error from backend
        isSubmittingRef.current = false;
        showToast(response.error, { type: "error" });
        setFlowState(ConsentFlowState.PENDING_PARENT_SIGNATURE);
      } else {
        // Success - API call completed without error
        // (response may have consent_complete, success, or neither - treat all as success)
        handleChange(path, true);
        setFlowState(ConsentFlowState.COMPLETE);
        // Keep isSubmittingRef true until user navigates away
      }
    } catch (e) {
      console.error("Error completing AFP consent", e);
      isSubmittingRef.current = false;

      if (e instanceof APIError && e.status === 400) {
        showToast(t("Please upload the parent-signed consent form first."), {
          type: "error",
        });
      } else {
        showToast(t("Something went wrong. Please try again."), {
          type: "error",
        });
        Sentry.captureException(e);
      }
      setFlowState(ConsentFlowState.PENDING_PARENT_SIGNATURE);
    }
  };

  // Handle back navigation
  const handleBack = async () => {
    if (prevSection) {
      try {
        await partiallySaveData();
      } catch (e) {
        if (e instanceof APIError && e.status === 400) {
          showToast(t("Error saving your progress"), { type: "error" });
        } else {
          showToast(t("Something went wrong, try again"), { type: "error" });
          Sentry.captureException(e);
        }
      }
      navigateToSection(prevSection);
    }
  };

  // Handle next step
  const handleNextStep = () => {
    if (flowState === ConsentFlowState.INITIAL) {
      if (!hasSignature) {
        setShowConsentRequiredModal(true);
        return;
      }
      // Has signature but not yet signed - send to backend
      handleSign();
    } else if (flowState === ConsentFlowState.PENDING_PARENT_SIGNATURE) {
      handleComplete();
    } else if (flowState === ConsentFlowState.COMPLETE) {
      // Already signed - just submit
      submit();
    }
  };

  // Loading state
  if (isPdfLoading || isStatusLoading) {
    return <Loader height="500px" />;
  }

  // Render based on flow state
  const renderContent = () => {
    // Complete state
    if (flowState === ConsentFlowState.COMPLETE) {
      return (
        <SectionMessage
          icon="check_circle"
          type="success"
          sectionTitle={t("Consent Complete")}
        >
          {t("Your AFP consent has been successfully recorded.")}
        </SectionMessage>
      );
    }

    // Pending parent signature state (under-18) - also show during COMPLETING
    if (
      flowState === ConsentFlowState.PENDING_PARENT_SIGNATURE ||
      flowState === ConsentFlowState.COMPLETING
    ) {
      return (
        <Stack gap="space-4">
          {PrivacyMessage}

          <CertnDocumentScroll
            presignedUrl={signedFormUrl || ""}
            isSigned={true}
            skipHasToScroll={true}
            label={documentReviewLabel}
          />

          <SectionMessage
            icon="info"
            type="info"
            sectionTitle={t("Parent or Guardian Consent Required")}
          >
            <Stack gap="space-3">
              <span>
                {t(
                  "Since you are under 18 years of age, we will require your parent or guardian to also consent to this application. Please follow the below instructions on how you can request your parent or guardian to provide their consent."
                )}
              </span>
              <ol
                className={styles.instructionsList}
                data-testid="afp-consent-under-18-instructions"
              >
                <li>{t("Download the signed consent form above.")}</li>
                <li>
                  {t(
                    "Print the form and have your parent or guardian sign it (or email it to them)."
                  )}
                </li>
                <li>{t("Scan the signed form.")}</li>
                <li>{t("Log back in and upload the form below:")}</li>
              </ol>
            </Stack>
          </SectionMessage>

          <FileUpload
            files={uploadedFiles}
            setFiles={setUploadedFiles}
            allowedFileTypes={DEFAULT_FILE_TYPES}
            requestUrl="/api/internal/applicant/generate-presigned-url/"
            bucketDirectory="applicant_uploads"
            caseId={caseId}
            uploadType={AfpUploadType.CONSENT_DOCUMENT}
            testID="afp-consent-parent-upload"
            maxSizeBytes={10 * 1024 * 1024}
            triggerButton={
              <BasicButton leftIcon="add">{t("Upload document")}</BasicButton>
            }
          />
        </Stack>
      );
    }

    // Initial state (and SIGNING) - show PDF and signature pad
    return (
      <Stack gap="space-4">
        {PrivacyMessage}

        <CertnDocumentScroll
          presignedUrl={presignedUrl}
          isSigned={documentReviewed || hasSignature}
          onChange={handleDocumentReviewChange}
          skipHasToScroll={true}
          label={documentReviewLabel}
        />

        <Signature
          onChange={handleSignatureChange}
          onClear={handleSignatureClear}
          disabled={isTransitioning}
          width={580}
          height={200}
          className={styles.signature}
        />
      </Stack>
    );
  };

  return (
    <Stack gap="space-4">
      <div className={styles.heading}>
        <Text as="h1" style="form/page-title" testID="afp-consent-title">
          {t("Australian Federal Police Check Consent")}
        </Text>

        {subLabel && (
          <Text
            style="form/body-primary"
            color="text-weak"
            testID="afp-consent-sub-title"
          >
            {subLabel}
          </Text>
        )}
      </div>

      {renderContent()}

      <div className={styles.actionBar}>
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={handleBack}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />

          <PrimaryButton
            onPress={handleNextStep}
            variant="brand-primary"
            isLoading={isTransitioning}
            isDisabled={isNextDisabled}
            testID="section-next-step-button"
          >
            {nextButtonText}
          </PrimaryButton>
        </ButtonGroup>
      </div>

      <ConsentRequiredModal
        show={showConsentRequiredModal}
        onClose={() => setShowConsentRequiredModal(false)}
        alternativeDialogContent={{
          header: t("Signature required"),
          content: (
            <Trans>
              Please review the consent form and provide your signature using
              the signature pad before continuing.
            </Trans>
          ),
        }}
      />
    </Stack>
  );
}

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    borderTop: "1px solid var(--border-base)",
    paddingTop: "calc(var(--space-4) - 1px)",
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
  }),
  signature: css({
    width: "100%",
    maxWidth: "100%",
  }),
  instructionsList: css({
    margin: 0,
    paddingLeft: "var(--space-5)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
    color: "var(--text-base)",
    fontSize: "var(--font-size-body-primary)",
    lineHeight: "var(--line-height-body-primary)",
  }),
};

AfpConsentControl.displayName = "AfpConsentControl";

export const AfpConsentControlRenderer =
  withCascadiaControlProps(AfpConsentControl);
